import type { Theme } from '@goodtime/engine';
import { type Biome } from '@goodtime/shared';

/**
 * Biome-panorama backdrop lookup, shared across every wheel-room view
 * (Lobby, Loadout, Reveal, Resolve, Payout, …). A Vite glob picks up
 * every PNG in {@code src/assets/map_bg/} and exposes a
 * {biomeId → url} map at runtime. Missing biomes fall back to {@code
 * null} — callers should use their own navy gradient as a fallback.
 */
const BACKDROP_MODULES = import.meta.glob<{ default: string }>(
  '../../assets/map_bg/*.png',
  { eager: true, query: '?url' },
);

const BACKDROP_BY_BIOME: Partial<Record<Biome['id'], string>> = Object.fromEntries(
  Object.entries(BACKDROP_MODULES)
    .map(([path, mod]) => {
      const id = path.match(/([^/]+)\.png$/)?.[1] ?? null;
      return id ? [id, mod.default] : null;
    })
    .filter((entry): entry is [string, string] => entry !== null),
);

/**
 * Theme id → biome id. Slot themes use product-level names
 * ('forest', 'underwater'); world-map biomes use storyworld names
 * ('kingdom', 'cove'). Frost / frontier / neon are aligned. Falls
 * back to 'kingdom' so an unknown theme still gets the Greendale
 * panorama as a sensible default rather than flat navy.
 */
export function biomeIdForTheme(theme: Theme | null | undefined): Biome['id'] {
  switch (theme?.id) {
    case 'forest':
      return 'kingdom';
    case 'underwater':
      return 'cove';
    case 'frost':
      return 'frost';
    case 'frontier':
      return 'frontier';
    case 'neon':
      return 'neon';
    default:
      return 'kingdom';
  }
}

/** Resolve the biome-panorama URL for a given theme, or {@code null}
 *  when the asset hasn't been commissioned yet. */
export function wheelBiomeBackdropUrl(theme: Theme | null | undefined): string | null {
  return BACKDROP_BY_BIOME[biomeIdForTheme(theme)] ?? null;
}
